import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import { Divider } from '@mui/material'
import { Card, Typography } from '@chilecompra/react-kit/components'
import { Skeleton, Toast } from '@chilecompra/react-kit'

import FormattedCurrencyComponent from '../../components/customs/FormattedCurrency.component'
import FormattedDateComponent from '../../components/customs/FormattedDate.component'

import { GridContainer, GridItem, Strong } from './DirectDealStepTwo.styles'

import {
  CURRENCY_TRANSLATION,
  DIRECT_DEAL_HONORARIUM_PERCENT_DICTIONARY,
  DIRECT_DEAL_STEP_TWO_TAX_EXEMPT_DICTIONARY,
  DIRECT_DEAL_SPECIAL_CAUSES,
  BINARY_CONFIRMATION_RESPONSE,
  UTM_COMPARISON_TYPES
} from '../../config/settings/constants'
import { formatCurrencyToNumber } from '../../modules/utils/formatters'
import { useImperativeFormRef } from '../../modules/hooks/useFormRef'

/**
 * The DirectDealStepTwoResume's container.
 */
const DirectDealStepTwoResume = props => {
  const {
    currency,
    currencies,
    formStepTwoRef,
    loading,
    taxes,
    causalUTMLimitError,
    directDealStepOneSheet,
    onSetCausalUTMLimitError
  } = props

  const [taxName, setTaxName] = useState('')
  const [taxTotal, setTaxTotal] = useState(0)
  const [total, setTotal] = useState(0)
  const [totalUTM, setTotalUTM] = useState(0)

  const [totalDelivery, setTotalDelivery] = useState(0)
  const [totalDescount, setTotalDescount] = useState(0)
  const [totalCharge, setTotalCharge] = useState(0)

  const dateToday = new Date()

  const getTotalCurriencies = currencyType => {
    if (currencies && currencies.length > 0) {
      return currencies.find(currency => currency.type === currencyType)?.valueClp || 0
    }

    return 0
  }
  const calculateTotalUTM = (utm, total, fixed = 2) => {
    return utm === 0 ? 0 : (total / utm).toFixed(fixed)
  }

  useEffect(() => {
    let taxSelected = null

    if (taxes) {
      taxSelected = taxes.find(t => t.value === formStepTwoRef.current.taxes?.value)
      if (taxSelected) {
        setTaxName(taxSelected?.name)
      }
    }

    const taxPercent = taxSelected?.percentage || 0
    const net = formStepTwoRef.current.netTotal || 0
    const delivery = formatCurrencyToNumber(formStepTwoRef.current.delivery?.value || '0', currency?.type)
    const charges = formatCurrencyToNumber(formStepTwoRef.current.charge?.value || '0', currency?.type)
    const discount = formatCurrencyToNumber(formStepTwoRef.current.discount?.value || '0', currency?.type)

    setTotalDelivery(delivery)
    setTotalCharge(charges)
    setTotalDescount(discount)

    const amountBeforeTaxes = Math.max(net + delivery + charges - discount, 0)
    const valueUTM = getTotalCurriencies(CURRENCY_TRANSLATION.UTM.cod)

    if (formStepTwoRef.current.taxes?.value) {
      let amountAfterTaxes = 0
      let honorariumAmount = 0

      if (
        formStepTwoRef.current.taxes?.value === DIRECT_DEAL_HONORARIUM_PERCENT_DICTIONARY.HONORARIUM_BENEFIT_SII ||
        formStepTwoRef.current.taxes?.value === DIRECT_DEAL_HONORARIUM_PERCENT_DICTIONARY.HONORARIUM
      ) {
        honorariumAmount = amountBeforeTaxes / (1 - taxPercent / 100)
        amountAfterTaxes = honorariumAmount - amountBeforeTaxes
      } else {
        amountAfterTaxes = amountBeforeTaxes * (taxPercent / 100)
      }

      if (currency?.type === CURRENCY_TRANSLATION.CLP.cod) {
        const taxTotal = Math.round(amountAfterTaxes)
        const total = Math.round(amountBeforeTaxes + amountAfterTaxes)

        setTaxTotal(taxTotal)
        setTotal(total)
        setTotalUTM(calculateTotalUTM(valueUTM, total))
      } else if (currency?.type === CURRENCY_TRANSLATION.CLF.cod) {
        const totalTaxFixed = amountAfterTaxes.toFixed(4)
        const totalFixed = parseFloat(amountBeforeTaxes) + parseFloat(totalTaxFixed)
        const valueCLF = getTotalCurriencies(currency.type)
        const totalCLFToCLP = totalFixed * valueCLF

        setTaxTotal(totalTaxFixed)
        setTotal(totalFixed)
        setTotalUTM(calculateTotalUTM(valueUTM, totalCLFToCLP))
      } else if (currency?.type === CURRENCY_TRANSLATION.USD.cod) {
        const totalTaxFixed = amountAfterTaxes.toFixed(2)
        const totalFixed = parseFloat(amountBeforeTaxes) + parseFloat(totalTaxFixed)
        const valueUSD = getTotalCurriencies(currency.type)
        const totalUSDToCLP = totalFixed * valueUSD

        setTaxTotal(totalTaxFixed)
        setTotal(totalFixed)
        setTotalUTM(calculateTotalUTM(valueUTM, totalUSDToCLP))
      } else if (currency?.type === CURRENCY_TRANSLATION.UTM.cod) {
        const totalTaxFixed = amountAfterTaxes.toFixed(4)
        const totalFixed = parseFloat(amountBeforeTaxes) + parseFloat(totalTaxFixed)

        setTaxTotal(totalTaxFixed)
        setTotal(totalFixed)
        setTotalUTM(totalFixed)
      } else if (currency?.type === CURRENCY_TRANSLATION.EUR.cod) {
        const totalTaxFixed = amountAfterTaxes.toFixed(2)
        const totalFixed = parseFloat(amountBeforeTaxes) + parseFloat(totalTaxFixed)
        const valueEUR = getTotalCurriencies(currency.type)
        const totalCLFToEUR = totalFixed * valueEUR

        setTaxTotal(totalTaxFixed)
        setTotal(totalFixed)
        setTotalUTM(calculateTotalUTM(valueUTM, totalCLFToEUR))
      }
    } else {
      if (currency?.type === CURRENCY_TRANSLATION.CLP.cod) {
        const total = Math.round(amountBeforeTaxes)

        setTotal(total)
        setTotalUTM(calculateTotalUTM(valueUTM, total))
      } else if (currency?.type === CURRENCY_TRANSLATION.CLF.cod) {
        const totalFixed = amountBeforeTaxes.toFixed(4)
        const valueCLF = getTotalCurriencies(currency.type)
        const totalCLFToCLP = totalFixed * valueCLF

        setTotal(totalFixed)
        setTotalUTM(calculateTotalUTM(valueUTM, totalCLFToCLP))
      } else if (currency?.type === CURRENCY_TRANSLATION.USD.cod) {
        const totalFixed = amountBeforeTaxes.toFixed(2)
        const valueUSD = getTotalCurriencies(currency.type)
        const totalUSDToCLP = totalFixed * valueUSD

        setTotal(totalFixed)
        setTotalUTM(calculateTotalUTM(valueUTM, totalUSDToCLP))
      } else if (currency?.type === CURRENCY_TRANSLATION.UTM.cod) {
        const totalFixed = amountBeforeTaxes

        setTotal(totalFixed)
        setTotalUTM(totalFixed)
      } else if (currency?.type === CURRENCY_TRANSLATION.EUR.cod) {
        const totalFixed = amountBeforeTaxes.toFixed(2)
        const valueEUR = getTotalCurriencies(currency.type)
        const totalEURToCLP = totalFixed * valueEUR

        setTotal(totalFixed)
        setTotalUTM(calculateTotalUTM(valueUTM, totalEURToCLP))
      }
    }
  }, [
    formStepTwoRef.current.charge?.value,
    formStepTwoRef.current.delivery?.value,
    formStepTwoRef.current.discount?.value,
    formStepTwoRef.current.netTotal,
    formStepTwoRef.current.taxes?.value
  ])

  useImperativeFormRef(
    formStepTwoRef,
    () => ({
      total
    }),
    [total]
  )

  useEffect(() => {
    const causeSelected = DIRECT_DEAL_SPECIAL_CAUSES[directDealStepOneSheet?.cause]
    const amount = Number(totalUTM)
    const isAmountValid = Number.isFinite(amount)

    let errorMessage = ''

    /**
     * If the totalUTM is invalid due to a failure in the CLP-UF-UTM daily value calculation service,
     * the amount restriction is omitted so as not to halt publication.
     */
    if (amount !== 0) {
      if (causeSelected && isAmountValid && (causeSelected.maxUTM !== null || causeSelected.minUTM !== null)) {
        const { comparisonType, maxUTM, minUTM } = causeSelected

        let isInvalid = false

        switch (comparisonType) {
          case UTM_COMPARISON_TYPES.LESS:
            if (maxUTM !== null && !(amount < maxUTM)) isInvalid = true

            break
          case UTM_COMPARISON_TYPES.LESS_EQUAL:
            if (maxUTM !== null && !(amount <= maxUTM)) isInvalid = true

            break
          case UTM_COMPARISON_TYPES.GREATER:
            if (minUTM !== null && !(amount > minUTM)) isInvalid = true

            break
          case UTM_COMPARISON_TYPES.GREATER_EQUAL:
            if (minUTM !== null && !(amount >= minUTM)) isInvalid = true

            break
          default:
            break
        }

        if (isInvalid) {
          errorMessage = (
            <>
              El monto total considerado para el trato directo no cumple los requisitos establecidos para sustentar el
              trato directo en la causal <Strong>{causeSelected.name}</Strong>
            </>
          )

          onSetCausalUTMLimitError(errorMessage)
          return
        }
      }

      const isAmountOverThousand = directDealStepOneSheet?.amount === BINARY_CONFIRMATION_RESPONSE[0].value
      const isAmountUnderThousand = directDealStepOneSheet?.amount === BINARY_CONFIRMATION_RESPONSE[1].value

      if (isAmountValid) {
        if ((isAmountOverThousand && amount <= 1000) || (isAmountUnderThousand && amount > 1000)) {
          errorMessage = (
            <>
              El monto total considerado para el trato directo{' '}
              <Strong>no cumple con el tramo de monto de la contratación señalado en el paso 1</Strong>
            </>
          )

          onSetCausalUTMLimitError(errorMessage)
          return
        }
      }

      onSetCausalUTMLimitError('')
    }
  }, [directDealStepOneSheet?.cause, directDealStepOneSheet?.amount, totalUTM, onSetCausalUTMLimitError])

  return (
    <GridContainer margin="33px 0" spacing={2}>
      <GridItem lg={8} md={8} sm={12} xs={12}>
        <Card padding="24px 16px">
          <GridContainer spacing={2}>
            <GridItem xs={12}>
              <Typography variant="body1" fontWeight="bold" tabIndex="0">
                Resumen
              </Typography>
            </GridItem>
            <GridItem xs={6}>
              <Typography variant="body2" fontWeight="bold" tabIndex="0">
                Subtotal neto
              </Typography>
            </GridItem>
            <GridItem xs={6} textAlign="right">
              {loading && <Skeleton width="80px" />}
              {!loading && (
                <Typography variant="h4" fontWeight="bold" tabIndex="0">
                  <FormattedCurrencyComponent
                    amount={formStepTwoRef.current.netTotal || 0}
                    currency={currency?.type}
                    includeSymbol
                  />
                </Typography>
              )}
            </GridItem>
            <GridItem xs={6}>
              <Typography variant="body2" fontSize="14px" tabIndex="0">
                Despacho general
              </Typography>
            </GridItem>
            <GridItem xs={6} textAlign="right">
              {loading && <Skeleton width="80px" />}
              {!loading && (
                <Typography variant="h4" tabIndex="0">
                  <FormattedCurrencyComponent amount={totalDelivery || 0} currency={currency?.type} includeSymbol />
                </Typography>
              )}
            </GridItem>
            <GridItem xs={6}>
              <Typography variant="body2" fontSize="14px" tabIndex="0">
                Cargos generales
              </Typography>
            </GridItem>
            <GridItem xs={6} textAlign="right">
              {loading && <Skeleton width="80px" />}
              {!loading && (
                <Typography variant="h4" tabIndex="0">
                  <FormattedCurrencyComponent amount={totalCharge || 0} currency={currency?.type} includeSymbol />
                </Typography>
              )}
            </GridItem>
            <GridItem xs={6}>
              <Typography variant="body2" fontSize="14px" tabIndex="0">
                Descuentos generales
              </Typography>
            </GridItem>
            <GridItem xs={6} textAlign="right">
              {loading && <Skeleton width="80px" />}
              {!loading && (
                <Typography variant="h4" tabIndex="0">
                  -
                  <FormattedCurrencyComponent amount={totalDescount || 0} currency={currency?.type} includeSymbol />
                </Typography>
              )}
            </GridItem>
            {formStepTwoRef.current.taxes?.value &&
              formStepTwoRef.current.taxes?.value !== DIRECT_DEAL_STEP_TWO_TAX_EXEMPT_DICTIONARY.EXEMPT &&
              formStepTwoRef.current.taxes?.value !== DIRECT_DEAL_STEP_TWO_TAX_EXEMPT_DICTIONARY.FREE_TRADE_ZONE && (
                <>
                  <GridItem xs={6}>
                    <Typography variant="body2" fontSize="14px" tabIndex="0">
                      Impuestos
                    </Typography>

                    <Typography variant="body2" fontSize="14px" color="black3" tabIndex="0">
                      {taxName}
                    </Typography>
                  </GridItem>
                  <GridItem xs={6} textAlign="right">
                    {loading && <Skeleton width="80px" />}
                    {!loading && (
                      <Typography variant="h4" tabIndex="0">
                        <FormattedCurrencyComponent amount={taxTotal || 0} currency={currency?.type} includeSymbol />
                      </Typography>
                    )}
                  </GridItem>
                </>
              )}

            <GridItem xs={12}>
              <Divider />
            </GridItem>
            <GridItem xs={6}>
              <Typography variant="h4" color="primary" fontWeight="bold" tabIndex="0">
                Monto total
              </Typography>
            </GridItem>
            <GridItem xs={6} textAlign="right">
              {loading && <Skeleton width="80px" />}
              {!loading && (
                <>
                  <Typography variant="h4" fontWeight="bold" tabIndex="0">
                    <FormattedCurrencyComponent amount={total || 0} currency={currency?.type} includeSymbol />
                  </Typography>
                  {currency?.type !== CURRENCY_TRANSLATION.UTM.cod && totalUTM !== 0 && (
                    <Typography variant="body2" fontSize="14px" color="black3" tabIndex="0">
                      <FormattedCurrencyComponent amount={totalUTM || 0} currency="UTM" /> UTM al día{' '}
                      <FormattedDateComponent date={dateToday} />
                    </Typography>
                  )}
                </>
              )}
            </GridItem>
          </GridContainer>
        </Card>

        {causalUTMLimitError && (
          <Toast margin="16px 0 0" severity="error">
            {causalUTMLimitError}
          </Toast>
        )}
      </GridItem>
    </GridContainer>
  )
}

DirectDealStepTwoResume.propTypes = {
  currency: PropTypes.shape({
    value: PropTypes.number,
    name: PropTypes.string,
    type: PropTypes.string,
    symbol: PropTypes.string
  }),
  currencies: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      name: PropTypes.string,
      valueClp: PropTypes.number
    })
  ),
  formStepTwoRef: PropTypes.shape({
    current: PropTypes.shape(Object)
  }),
  loading: PropTypes.bool,
  taxes: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, name: PropTypes.string })),
  causalUTMLimitError: PropTypes.string,
  directDealStepOneSheet: PropTypes.shape({
    amount: PropTypes.string,
    cause: PropTypes.string
  }),
  onSetCausalUTMLimitError: PropTypes.func
}

export default DirectDealStepTwoResume
